import { useEffect, useMemo } from 'react';
import { useRouter } from 'next/router';
import { useQuery } from 'react-query';

import { showError } from '@utils';
import { SERVICE_POINT_STATUSES } from '@constants';

import { useLayout } from 'hooks/layout';
import { QUERY_KEYS } from 'constants/queryKeys';
import {
  fleetVehiclesServices,
  servicePointsServices,
  vehiclesServices,
} from '@services';
import {
  getServicePointsMap,
  handleEnums,
  transformVehiclesToGridRows,
  normalizeServicePoints,
  getParamsAndPayloadFleetVehicles,
} from './utils';
import { VEHICLES_PAGE_SIZE } from './constants';

const SERVICE_POINTS_PAGE_SIZE = 10000;

export const useFleetVehiclesOperations = () => {
  const { tenant } = useLayout();

  const router = useRouter();

  const { isReady } = router;

  const fetchFleetVehiclesEnums = async () => {
    const results = await Promise.all([
      fleetVehiclesServices.getVehicleSourcingTypes(),
      fleetVehiclesServices.getVehicleDealers(),
      fleetVehiclesServices.getVehicleColors(),
      fleetVehiclesServices.getInsurances(),
      vehiclesServices.getVehiclesEnums(),
    ]);

    return {
      sourcingTypes: results[0],
      dealers: results[1],
      colors: results[2],
      insurances: results[3],
      vehiclesEnums: results[4],
    };
  };

  const fleetVehiclesEnumsResponse = useQuery(
    [QUERY_KEYS.GET_FLEET_VEHICLES_ENUMS_KEY],
    fetchFleetVehiclesEnums,
    { enabled: isReady }
  );

  const enums = useMemo(
    () => handleEnums(fleetVehiclesEnumsResponse?.data || {}),
    [fleetVehiclesEnumsResponse?.data]
  );
  const procurementStageIds = useMemo(
    () => enums?.procurementStageIds || [],
    [enums?.procurementStageIds]
  );

  const page = router.query?.page || 1;
  const pageSize = router.query?.pageSize || VEHICLES_PAGE_SIZE;
  const orderBy = router.query?.orderBy || '';
  const order = router.query?.order || '';
  const stage = router.query?.stage || '';
  const query = router.query?.query || '';
  const categoryClass = router.query?.categoryClass || '';
  const receiptDate = router.query?.receiptDate || '';
  const plannedReceiptDate = router.query?.plannedReceiptDate || '';
  const licenseIssuanceDate = router.query?.licenseIssuanceDate || '';
  const plannedDownpaymentDate = router.query?.plannedDownpaymentDate || '';

  const { params: cleanedParams, payload: fleetPayload } = useMemo(
    () =>
      getParamsAndPayloadFleetVehicles({
        orderBy,
        order,
        page,
        pageSize: parseInt(pageSize, 10) || VEHICLES_PAGE_SIZE,
        tenant,
        stage,
        query,
        categoryClass,
        receiptDate,
        plannedReceiptDate,
        licenseIssuanceDate,
        plannedDownpaymentDate,
        procurementStageIds,
      }),
    [
      orderBy,
      order,
      page,
      pageSize,
      tenant,
      stage,
      query,
      categoryClass,
      receiptDate,
      plannedReceiptDate,
      licenseIssuanceDate,
      plannedDownpaymentDate,
      procurementStageIds,
    ]
  );

  const vehiclesResponse = useQuery(
    [QUERY_KEYS.GET_FLEET_VEHICLES_KEY, cleanedParams, fleetPayload],
    () => {
      return fleetVehiclesServices.getFilteredVehicles({
        params: cleanedParams,
        payload: fleetPayload,
      });
    },
    { enabled: isReady }
  );

  const servicePointsResponse = useQuery(
    [QUERY_KEYS.GET_SERVICE_POINTS_KEY],
    () => {
      const payload = {
        page: 1,
        pageSize: SERVICE_POINTS_PAGE_SIZE,
        type: 0,
        field: null,
        operator: null,
        value: [
          {
            field: 'status',
            operator: 0,
            type: 5,
            value: SERVICE_POINT_STATUSES.active, // fetch only acticated
          },
        ],
      };
      return servicePointsServices.getServicePoints(payload);
    }
  );

  useEffect(() => {
    if (vehiclesResponse?.error) {
      showError(vehiclesResponse.error);
    }
    if (fleetVehiclesEnumsResponse.error) {
      showError(fleetVehiclesEnumsResponse.error);
    }
  }, [vehiclesResponse?.error, fleetVehiclesEnumsResponse.error]);
  const servicePoints = normalizeServicePoints(
    servicePointsResponse?.data?.data || []
  );
  const servicePointsMap = getServicePointsMap(servicePoints);

  const total = vehiclesResponse?.data?.meta?.total;
  const loading =
    vehiclesResponse?.isLoading || fleetVehiclesEnumsResponse.isLoading;

  const vehicles = transformVehiclesToGridRows({
    vehicles: vehiclesResponse?.data?.data || [],
    enums,
    servicePoints,
    servicePointsMap,
  });

  return {
    page,
    total,
    loading,
    vehicles,
    tenant,
    pageSize: parseInt(pageSize, 10) || VEHICLES_PAGE_SIZE,
    servicePoints: servicePointsMap,
    enums,
    fleetVehiclesQueryParams: cleanedParams,
    fleetVehiclesFilterQueryPayload: fleetPayload,
    getVehicles: vehiclesResponse.refetch,
  };
};
